import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";

import { useSidebar } from "../context/SidebarContext";
import {
  isNavGroup,
  isNavPathActive,
  navSections,
  sectionContainsPath,
  type NavEntry,
} from "../config/navConfig";
import {
  BoxCubeIcon,
  CalenderIcon,
  ChevronDownIcon,
  GridIcon,
  HorizontaLDots,
  ListIcon,
  PageIcon,
  PieChartIcon,
  PlugInIcon,
  TableIcon,
  UserCircleIcon,
  BoltIcon,
  MoreDotIcon,
  TaskIcon,
  DollarLineIcon,
  LockIcon,
} from "../icons";
import SidebarWidget from "./SidebarWidget";
import { TextLogo } from "../components/common/TextLogo";

const pathIcons: Record<string, React.ReactNode> = {
  "/": <PieChartIcon />,
  "/bookings": <CalenderIcon />,
  "/drivers": <UserCircleIcon />,
  "/trip-issues": <TaskIcon />,
  "/ride-reviews": <TaskIcon />,
  "/statements": <DollarLineIcon />,
  "/service-pauses": <BoltIcon />,
  "/price-checker": <ListIcon />,
  "/flash-sales": <PageIcon />,
  "/coupons": <PageIcon />,
  "/dead-legs": <TableIcon />,
  "/route-fixed-fares": <ListIcon />,
  "/per-mile-prices": <PieChartIcon />,
  "/zones": <GridIcon />,
  "/location-charges": <PlugInIcon />,
  "/location-prices": <ListIcon />,
  "/postcode-area-prices": <BoxCubeIcon />,
  "/meet-greet-charges": <GridIcon />,
  "/vehicle-time-prices": <CalenderIcon />,
  "/special-prices": <DollarLineIcon />,
  "/luggage": <BoxCubeIcon />,
  "/more-pricing-options": <MoreDotIcon />,
  "/number-of-vehicles": <GridIcon />,
  "/notice-periods": <CalenderIcon />,
  "/trip-range": <ListIcon />,
  "/operating-hours": <CalenderIcon />,
  "/vehicle-type": <GridIcon />,
  "/vehicles": <GridIcon />,
  "/customers": <UserCircleIcon />,
  "/admin-user": <UserCircleIcon />,
  "/manage-sessions": <LockIcon />,
  "/role": <LockIcon />,
  "/permission": <LockIcon />,
  "/user-role-mapping": <LockIcon />,
  "/role-permission-mapping": <LockIcon />,
  "/admin-user-address": <UserCircleIcon />,
};

const groupIcons: Record<string, React.ReactNode> = {
  "Access Control": <LockIcon />,
};

function iconForEntry(entry: NavEntry): React.ReactNode {
  if (isNavGroup(entry)) {
    return groupIcons[entry.name] ?? <ListIcon />;
  }
  return pathIcons[entry.path] ?? <PageIcon />;
}

function entryIsActive(entry: NavEntry, pathname: string): boolean {
  if (isNavGroup(entry)) {
    return entry.subItems.some((sub) => isNavPathActive(sub.path, pathname));
  }
  return isNavPathActive(entry.path, pathname);
}

const AppSidebar: React.FC = () => {
  const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar();
  const location = useLocation();

  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(navSections.map((section) => [section.id, true])),
  );
  const [openSubmenu, setOpenSubmenu] = useState<{
    sectionId: string;
    index: number;
  } | null>(null);
  const [subMenuHeight, setSubMenuHeight] = useState<Record<string, number>>({});
  const subMenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const sidebarOpen = isExpanded || isHovered || isMobileOpen;

  useEffect(() => {
    const nextOpen: Record<string, boolean> = {};
    navSections.forEach((section) => {
      nextOpen[section.id] =
        openSections[section.id] ?? sectionContainsPath(section, location.pathname);
    });
    navSections.forEach((section) => {
      if (sectionContainsPath(section, location.pathname)) {
        nextOpen[section.id] = true;
      }
    });
    setOpenSections((prev) => ({ ...prev, ...nextOpen }));

    let submenuMatched = false;
    navSections.forEach((section) => {
      section.entries.forEach((entry, index) => {
        if (isNavGroup(entry)) {
          entry.subItems.forEach((subItem) => {
            if (isNavPathActive(subItem.path, location.pathname)) {
              setOpenSubmenu({ sectionId: section.id, index });
              submenuMatched = true;
            }
          });
        }
      });
    });
    if (!submenuMatched) setOpenSubmenu(null);
  }, [location.pathname]);

  useEffect(() => {
    if (openSubmenu !== null) {
      const key = `${openSubmenu.sectionId}-${openSubmenu.index}`;
      if (subMenuRefs.current[key]) {
        setSubMenuHeight((prevHeights) => ({
          ...prevHeights,
          [key]: subMenuRefs.current[key]?.scrollHeight || 0,
        }));
      }
    }
  }, [openSubmenu, openSections]);

  const toggleSection = (sectionId: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handleSubmenuToggle = (sectionId: string, index: number) => {
    setOpenSubmenu((prev) => {
      if (prev && prev.sectionId === sectionId && prev.index === index) return null;
      return { sectionId, index };
    });
  };

  const renderEntry = (entry: NavEntry, sectionId: string, index: number) => {
    const active = entryIsActive(entry, location.pathname);

    if (isNavGroup(entry)) {
      const submenuKey = `${sectionId}-${index}`;
      const isSubmenuOpen =
        openSubmenu?.sectionId === sectionId && openSubmenu?.index === index;

      return (
        <li key={entry.name}>
          <button
            type="button"
            onClick={() => handleSubmenuToggle(sectionId, index)}
            className={`menu-item group ${
              active || isSubmenuOpen ? "menu-item-active" : "menu-item-inactive"
            } cursor-pointer ${!sidebarOpen ? "lg:justify-center" : "lg:justify-start"}`}
          >
            <span
              className={
                active || isSubmenuOpen
                  ? "menu-item-icon-active"
                  : "menu-item-icon-inactive"
              }
            >
              {iconForEntry(entry)}
            </span>
            {sidebarOpen && <span className="menu-item-text">{entry.name}</span>}
            {sidebarOpen && (
              <ChevronDownIcon
                className={`ml-auto h-5 w-5 transition-transform duration-200 ${
                  isSubmenuOpen ? "rotate-180 text-brand-500" : ""
                }`}
              />
            )}
          </button>
          {sidebarOpen && (
            <div
              ref={(el) => {
                subMenuRefs.current[submenuKey] = el;
              }}
              className="overflow-hidden transition-all duration-300"
              style={{
                height: isSubmenuOpen ? `${subMenuHeight[submenuKey] ?? 0}px` : "0px",
              }}
            >
              <ul className="ml-9 mt-2 space-y-1">
                {entry.subItems.map((subItem) => (
                  <li key={subItem.path}>
                    <Link
                      to={subItem.path}
                      className={`menu-dropdown-item ${
                        isNavPathActive(subItem.path, location.pathname)
                          ? "menu-dropdown-item-active"
                          : "menu-dropdown-item-inactive"
                      }`}
                    >
                      {subItem.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </li>
      );
    }

    return (
      <li key={entry.path}>
        <Link
          to={entry.path}
          className={`menu-item group ${
            active ? "menu-item-active" : "menu-item-inactive"
          }`}
        >
          <span
            className={
              active ? "menu-item-icon-active" : "menu-item-icon-inactive"
            }
          >
            {iconForEntry(entry)}
          </span>
          {sidebarOpen && <span className="menu-item-text">{entry.name}</span>}
        </Link>
      </li>
    );
  };

  return (
    <aside
      className={`fixed left-0 top-0 z-50 mt-16 flex h-screen flex-col border-r border-gray-200 bg-white px-5 text-gray-900 transition-all duration-300 ease-in-out dark:border-gray-800 dark:bg-gray-900 lg:mt-0
        ${
          isExpanded || isMobileOpen
            ? "w-[290px]"
            : isHovered
              ? "w-[290px]"
              : "w-[90px]"
        }
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`flex py-8 ${
          !isExpanded && !isHovered ? "lg:justify-center" : "justify-start"
        }`}
      >
        <Link to="/" className="px-5">
          {sidebarOpen ? <TextLogo /> : <TextLogo variant="compact" />}
        </Link>
      </div>

      <div className="no-scrollbar flex flex-col overflow-y-auto duration-300 ease-linear">
        <nav className="mb-6">
          <div className="flex flex-col gap-2">
            {navSections.map((section) => {
              const isSectionOpen = openSections[section.id] ?? true;
              const sectionActive = sectionContainsPath(section, location.pathname);

              return (
                <div key={section.id} className="rounded-xl">
                  <button
                    type="button"
                    onClick={() => toggleSection(section.id)}
                    className={`mb-2 flex w-full items-center text-xs uppercase leading-[20px] text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-300 ${
                      !sidebarOpen ? "lg:justify-center" : "justify-start"
                    } ${sectionActive ? "text-brand-500 dark:text-brand-400" : ""}`}
                    aria-expanded={isSectionOpen}
                  >
                    {sidebarOpen ? (
                      <>
                        <span className="flex-1 text-left">{section.label}</span>
                        <ChevronDownIcon
                          className={`h-4 w-4 shrink-0 transition-transform duration-200 ${
                            isSectionOpen ? "rotate-180" : ""
                          }`}
                        />
                      </>
                    ) : (
                      <HorizontaLDots />
                    )}
                  </button>

                  {(isSectionOpen || !sidebarOpen) && (
                    <ul className="flex flex-col gap-3 pb-2">
                      {section.entries.map((entry, index) =>
                        renderEntry(entry, section.id, index),
                      )}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </nav>
        {sidebarOpen ? <SidebarWidget /> : null}
      </div>
    </aside>
  );
};

export default AppSidebar;
